/**
 * TanStack Query Client Configuration
 *
 * Configures React Query with sensible defaults optimized for:
 * - Offline-first PWA experience
 * - Nigerian network conditions (retry with backoff)
 * - Efficient caching and background refetching
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Default stale time - 5 minutes
 * Data is considered fresh and won't trigger background refetch
 */
const STALE_TIME = 5 * 60 * 1000;

/**
 * Default garbage collection time - 30 minutes
 * Inactive data remains in cache for offline access
 */
const GC_TIME = 30 * 60 * 1000;

/**
 * Create and configure the QueryClient instance
 *
 * @returns Configured QueryClient
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data is considered fresh for 5 minutes
        staleTime: STALE_TIME,

        // Keep unused data in cache for 30 minutes (offline support)
        gcTime: GC_TIME,

        // Retry failed requests twice with exponential backoff
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

        // Refetch on window focus (user returns to app)
        refetchOnWindowFocus: true,

        // Refetch when network reconnects (important for offline-first)
        refetchOnReconnect: true,

        // Don't refetch on mount if data is still fresh
        refetchOnMount: true,

        // Network mode: always try to fetch (offline support handled by service worker)
        networkMode: 'offlineFirst',
      },
      mutations: {
        // Retry mutations once on failure
        retry: 1,
        retryDelay: 1000,

        // Network mode: offline mutations are queued
        networkMode: 'offlineFirst',
      },
    },
  });
}

/**
 * Global QueryClient instance
 *
 * Use this singleton throughout the app for consistent cache behavior.
 * For testing, create a new instance with createQueryClient().
 */
export const queryClient = createQueryClient();
